import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Default UI-Avatars URL that generates placeholder avatars with initials
const getInitialsAvatarUrl = (firstName, lastName) => {
  const name = `${firstName || ''}+${lastName || ''}`;
  const background = '0D8ABC'; // Blue background
  return `https://ui-avatars.com/api/?name=${name}&background=${background}&color=fff`;
};

/**
 * Reusable component for displaying profile pictures with proper error handling
 */
const ProfilePicture = ({ 
  src, 
  alt = 'Profile', 
  className = '',
  size = 'md', 
  firstName = '',
  lastName = '',
  round = true
}) => {
  const [imageError, setImageError] = useState(false);

  // Determine size class based on size prop
  const sizeClass = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
    '2xl': 'h-32 w-32'
  }[size] || 'h-10 w-10';

  // Determine border radius
  const roundClass = round ? 'rounded-full' : 'rounded-md';

  // Get image URL with proper API prefix if needed
  const getImageUrl = () => {
    if (!src || imageError) {
      return getInitialsAvatarUrl(firstName, lastName);
    }
    
    try {
      if (src.startsWith('http')) {
        return src;
      } else if (src.startsWith('/')) {
        // Try to get VITE_API_URL first (for Vite projects)
        const baseUrl = import.meta.env?.VITE_API_URL || process.env.REACT_APP_API_URL || '';
        // Remove /api suffix if present for image URLs
        const apiBaseUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;
        return `${apiBaseUrl}${src}`;
      } else {
        // Try to get VITE_API_URL first (for Vite projects)
        const baseUrl = import.meta.env?.VITE_API_URL || process.env.REACT_APP_API_URL || '';
        // Remove /api suffix if present for image URLs
        const apiBaseUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;
        return `${apiBaseUrl}/${src}`;
      }
    } catch (e) {
      console.error('Error processing image URL:', e);
      return getInitialsAvatarUrl(firstName, lastName);
    }
  };

  // Reset the error state if src changes
  React.useEffect(() => {
    setImageError(false);
  }, [src]);

  // Handle image loading error
  const handleImageError = () => {
    console.warn('Image failed to load:', src);
    setImageError(true);
  };

  return (
    <img
      src={getImageUrl()}
      alt={alt || `${firstName} ${lastName}`.trim() || 'Profile'}
      className={`${sizeClass} ${roundClass} object-cover ${className}`}
      onError={handleImageError}
    />
  );
};

ProfilePicture.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  round: PropTypes.bool
};

export default ProfilePicture;
